import { putConfirmationError } from '@essent/extension-offer';
import type { Action } from '@ngrx/store';
import { createReducer, on } from '@ngrx/store';
import type { FormGroupState } from 'ngrx-forms';
import {
  createFormGroupState,
  markAsDirty,
  markAsTouched,
  markAsUnsubmitted,
  onNgrxForms,
  onNgrxFormsAction,
  reset,
  ResetAction,
  setUserDefinedProperty,
  setValue,
  updateGroup,
  validate,
  wrapReducerWithFormStateUpdate,
} from 'ngrx-forms';
import { requiredTrue } from 'ngrx-forms/validation';

import { submitConfirmationForm } from './confirmation.actions';

export const CONFIRMATION_FORM_ID = 'confirmationForm';
export const customerHasConsentedToTermsControlId = `${CONFIRMATION_FORM_ID}.customerHasConsentedToTerms`;
export const energyAdvicePhoneAcceptedFormControlID = `${CONFIRMATION_FORM_ID}.energyAdvicePhoneAccepted`;
export const sustainabilityAdvicePhoneAcceptedFormControlID = `${CONFIRMATION_FORM_ID}.sustainabilityAdvicePhoneAccepted`;

export interface ConfirmationState {
  formState: FormGroupState<ConfirmationFormState>;
  successPage?: string;
}

export interface ConfirmationFormState {
  customerHasConsentedToTerms: boolean;
  energyAdvicePhoneAccepted?: boolean;
  sustainabilityAdvicePhoneAccepted?: boolean;
  smartChargingAddonAccepted: boolean;
}

export const confirmationInitialFormState: ConfirmationFormState = {
  customerHasConsentedToTerms: false,
  energyAdvicePhoneAccepted: false,
  sustainabilityAdvicePhoneAccepted: false,
  smartChargingAddonAccepted: false,
};

export const confirmationInitialFormGroupState =
  createFormGroupState<ConfirmationFormState>(
    CONFIRMATION_FORM_ID,
    confirmationInitialFormState
  );

export const confirmationInitialState: ConfirmationState = {
  formState: confirmationInitialFormGroupState,
};

export const validateForm = (state: ConfirmationState) =>
  updateGroup<ConfirmationFormState>({
    customerHasConsentedToTerms: validate(requiredTrue),
  })(state.formState);

export const resetForm = (state: ConfirmationState) => {
  return {
    ...state,
    formState: reset(setValue(state.formState, confirmationInitialFormState)),
  };
};

const _reducer = createReducer(
  confirmationInitialState,
  onNgrxForms(),
  on(submitConfirmationForm, (state, action) => {
    let formState = state.formState;
    formState = markAsDirty(formState);
    formState = markAsTouched(formState);
    formState = setUserDefinedProperty('error', undefined)(formState);

    return {
      ...state,
      formState,
      successPage: action.payload.successPage,
    };
  }),
  onNgrxFormsAction(ResetAction, (state, action) => {
    if (action.controlId === CONFIRMATION_FORM_ID) {
      return resetForm(state);
    }
    return state;
  }),
  on(putConfirmationError, (state) => {
    const unsubmittedFormState = markAsUnsubmitted(state.formState);
    const formStateWithError = setUserDefinedProperty('error', {
      technicalError: true,
    })(unsubmittedFormState);

    return {
      ...state,
      formState: formStateWithError,
    };
  })
);

const wrappedReducer = wrapReducerWithFormStateUpdate(
  _reducer,
  (state) => state.formState,
  (_, state) => validateForm(state)
);

export function confirmationFormReducer(
  state: ConfirmationState = confirmationInitialState,
  action: Action
) {
  return wrappedReducer(state, action);
}
